<template lang="pug">
section(v-bind="sectionAttributes")
    div(:class="contentClasses")
        template(v-if="data.widgets")
            template(v-for="widget in data.widgets")
                slot(
                    :data="widget"
                    :spaceLeft="spaceLeft"
                )

    div(
        v-if="backgroundVideo.filename && !backgroundImageSrc && (data.isSticky !== 'true')"
        class="z-[-1] absolute inset-0 w-full h-full"
    )
        LazyVideo(
            :isLazy="shouldLazyLoad"
            :videoUrl="backgroundVideo.filename"
            :posterImageUrl="backgroundVideo.poster"
            class="absolute inset-0 object-cover h-full"
            width="100%" height="100%"
        )

    Lightbox(
        v-if="activeOverlay == lightboxName"
        :uniqueKey="lightboxName"
        :items="lightboxImages"
        :options="lightboxOptions"
        @close="hideLightbox"
    )
</template>

<script>
import LazyVideo from '@/components/lazyload/lazyVideo'

import {
    generateBackgroundColorClass,
    generatePaddingClasses,
    generateMarginClasses,
    generateFontColorClass,
    getMinHeight,
    backgroundImageMixin,
} from '../../helpers'

const Lightbox = () =>
    import(/* webpackChunkName: "photoswipe-lightbox" */ '@/components/overlay/photoswipeLightbox')

export default {
    name: 'Section',
    components: {
        Lightbox,
        LazyVideo,
    },
    mixins: [backgroundImageMixin],
    provide() {
        return {
            $lightbox: {
                add: this.addImage,
                open: this.showLightbox,
            },
        }
    },
    props: {
        data: {
            type: Object,
            default() {
                return {}
            },
        },
    },
    data() {
        return {
            lightboxImages: [],
            lightboxOptions: {
                index: 0,
                closeOnScroll: false,
                history: false,
                shareEl: false,
                fullscreenEl: false,
            },
            shouldLazyLoad: false,
        }
    },
    computed: {
        spaceLeft() {
            return {
                mobile: 768,
                tablet: 1024,
                desktop: this.data.isFullWidth === 'true' ? 1920 : 1280,
            }
        },
        sectionAttributes() {
            // Destructure the bg image object given by our bg image mixin
            const {
                class: bgImageClass,
                style: bgImageStyle,
                ...bgImageAttrs
            } = this.backgroundImageAttrs

            return {
                ...bgImageAttrs,
                class: [this.sectionClasses, bgImageClass],
                style: {
                    minHeight: this.sectionMinHeight,
                    ...bgImageStyle,
                },
            }
        },
        sectionClasses() {
            const classes = [
                // Margin Classes
                generateMarginClasses(
                    this.data.mobileMargin,
                    this.data.tabletMargin,
                    this.data.desktopMargin
                ),
                // Font color class to apply to the whole section.
                generateFontColorClass(this.data.textColor),
                {
                    relative: this.data.isSticky === 'false',
                    'sticky top-0 z-[2]': this.data.isSticky === 'true',
                },
            ]

            if (this.data.backgroundColorIsFullWidth === 'true')
                classes.push(generateBackgroundColorClass(this.data.backgroundColor))

            if (this.data.verticalAlignment) {
                classes.push('flex flex-col')

                switch (this.data.verticalAlignment) {
                    case 'TOP':
                        classes.push('justify-start')
                        break

                    case 'MIDDLE':
                        classes.push('justify-center')
                        break

                    case 'BOTTOM':
                        classes.push('justify-end')
                        break
                }
            }

            return classes
        },
        contentClasses() {
            const classes = [
                // Padding Classes
                generatePaddingClasses(
                    this.data.mobilePadding,
                    this.data.tabletPadding,
                    this.data.desktopPadding
                ),
                {
                    'lg:container': this.data.isFullWidth !== 'true', // Is the content fullwidth?
                },
            ]

            if (this.data.backgroundColorIsFullWidth === 'false')
                classes.push(generateBackgroundColorClass(this.data.backgroundColor))

            return classes
        },
        sectionMinHeight() {
            const minHeight = getMinHeight(this.data, this.$device)

            switch (minHeight) {
                case 'SMALL':
                    return this.$device.isMobile ? '250px' : '350px'
                case 'MEDIUM':
                    return this.$device.isMobile ? '350px' : '450px'
                case 'LARGE':
                    return this.$device.isMobile ? '450px' : '550px'
                default:
                    return null
            }
        },
        activeOverlay() {
            return this.$overlay.state.activeOverlay
        },
        lightboxName() {
            return this.data._uid
        },
        backgroundVideo() {
            const backgroundVideo = {
                filename: '',
                poster: '',
            }

            if (this.data.mobileBackgroundVideo?.filename) {
                backgroundVideo.filename = this.data.mobileBackgroundVideo.filename
                if (this.data.mobileBackgroundPoster?.filename)
                    backgroundVideo.poster = this.data.mobileBackgroundPoster.filename
            }

            if (this.data.tabletBackgroundVideo?.filename && !this.$device.isMobile) {
                backgroundVideo.filename = this.data.tabletBackgroundVideo.filename
                if (this.data.tabletBackgroundPoster?.filename)
                    backgroundVideo.poster = this.data.tabletBackgroundPoster.filename
            }

            if (this.data.desktopBackgroundVideo?.filename && this.$device.isDesktop) {
                backgroundVideo.filename = this.data.desktopBackgroundVideo.filename
                if (this.data.desktopBackgroundPoster?.filename)
                    backgroundVideo.poster = this.data.desktopBackgroundPoster.filename
            }

            // Format to use the CDN
            backgroundVideo.filename = this.$storyblok.formatSrc(backgroundVideo.filename)

            // Reduce poster image quality to 50% to improve LCP
            if (backgroundVideo.poster)
                backgroundVideo.poster =
                    this.$storyblok.formatSrc(backgroundVideo.poster) + '/m/filters:quality(50)'

            return backgroundVideo
        },
    },
    created() {
        // Needed for the LazyVideo component
        // backgroundImageMixin doesn't run unless section has a background image
        this.shouldLazyLoad = this.$story.shouldLazyLoad(false)
    },
    mounted() {
        this.$overlay.registerUrlKey(this.lightboxName)
    },
    methods: {
        // Add an image object to the lightbox
        addImage(src, width, height) {
            return this.lightboxImages.push({ src, w: width, h: height }) - 1
        },
        showLightbox(index) {
            if (this.$storyblok.isEditing) return

            this.lightboxOptions.index = index
            this.$overlay.show(this.lightboxName)
        },
        hideLightbox() {
            this.$overlay.hide()
        },
    },
}
</script>
